import { useQuery } from '@tanstack/react-query';
import { Modal } from './ui/Modal';
import { apiClient } from '../lib/api';
import { formatNumber, formatCurrency, formatPercent } from '../lib/utils';
import { CountryFlag } from './ui/CountryFlag';
import { useOfferLookup, usePublisherLookup } from '../hooks/useLookups';
import {
  Activity,
  DollarSign,
  Globe,
  Tag,
  Smartphone,
  Wifi,
  AlertTriangle,
  Bot,
  Target,
  TrendingUp,
} from 'lucide-react';

interface SubIdDetailModalProps {
  /** Pass the publisher.id, OR null for "unlinked" group, OR undefined to keep modal closed */
  publisherId: number | null | undefined;
  /** Optional sub_id filter for unlinked rows. When publisherId=null and a
   *  subIdHint is passed (e.g. "CEO-IG" from an unlinked-but-with-sub_id
   *  conversion row), the detail view scopes to just that sub_id rather
   *  than aggregating ALL unlinked conversions. */
  subIdHint?: string;
  onClose: () => void;
  range?: 'today' | 'yesterday' | '7d' | '30d' | '90d' | 'custom';
  customFrom?: string;
  customTo?: string;
}

interface DimRow {
  clicksTotal: number;
  clicksQuality: number;
  clicksBot: number;
  conversions?: number;
  revenue?: number;
}

interface DetailResponse {
  publisherId: number | null;
  range: string;
  days: number;
  totals: {
    conversionsApproved: number;
    conversionsPending: number;
    conversionsRejected: number;
    conversionsReversed: number;
    revenueApproved: number;
    revenuePending: number;
    payoutApproved: number;
    clicksTotal?: number;
    clicksBot?: number;
    clicksQuality?: number;
  };
  byCountry: Array<DimRow & { country: string | null }>;
  byOffer: Array<DimRow & { offerId: number | null }>;
  byDevice: Array<{
    device: string | null;
    clicksTotal: number;
    clicksQuality: number;
    clicksBot: number;
  }>;
  bySource: Array<{
    source: string | null;
    clicksTotal: number;
    clicksQuality: number;
    clicksBot: number;
  }>;
  timeseries: Array<{
    date: string;
    clicksTotal: number;
    clicksQuality: number;
    clicksBot: number;
    conversions: number;
    revenue: number;
  }>;
}

export function SubIdDetailModal({
  publisherId,
  subIdHint,
  onClose,
  range = '30d',
  customFrom,
  customTo,
}: SubIdDetailModalProps) {
  const open = publisherId !== undefined;
  const offerLookup = useOfferLookup();
  const publisherLookup = usePublisherLookup();

  const detailKey = publisherId === null ? 'unlinked' : String(publisherId);
  const query = useQuery({
    // Include subIdHint in queryKey so drilling into different unlinked
    // sub_ids fetches distinct data (cache busts correctly).
    queryKey: [
      'sub-detail',
      detailKey,
      range,
      customFrom ?? '',
      customTo ?? '',
      subIdHint ?? '',
    ],
    queryFn: () =>
      apiClient.get<DetailResponse>(`/api/v1/stats/sub/${detailKey}/detail`, {
        range,
        ...(range === 'custom' && customFrom && customTo
          ? { from: customFrom, to: customTo }
          : {}),
        // Only send sub_id filter when publisherId is null (unlinked drill-down)
        // and a hint is present. Registered-publisher detail doesn't need it
        // because publisher_id already disambiguates.
        ...(publisherId === null && subIdHint ? { sub_id: subIdHint } : {}),
      }),
    enabled: open,
  });

  const data = query.data;
  const subTitle =
    publisherId == null
      ? subIdHint
        ? `Unlinked · ${subIdHint}`
        : 'Unlinked Conversions'
      : publisherLookup.getName(publisherId);

  // Derived metrics for the KPI strip — done outside JSX so the calculations
  // are explicit and easy to reason about, not buried in template literals.
  const kpis = (() => {
    if (!data) return null;
    const total = data.totals.clicksTotal ?? 0;
    const quality = data.totals.clicksQuality ?? 0;
    const bots = data.totals.clicksBot ?? 0;
    const convs = data.totals.conversionsApproved;
    const revenue = data.totals.revenueApproved;
    const cr = quality > 0 ? (convs / quality) * 100 : 0;
    const epc = quality > 0 ? revenue / quality : 0;
    const botRate = total > 0 ? (bots / total) * 100 : 0;
    return { total, quality, bots, convs, revenue, cr, epc, botRate };
  })();

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Sub Detail · ${subTitle}`}
      size="xl"
    >
      {query.isLoading && (
        <div className="text-center py-12 text-text-tertiary font-mono text-sm">
          loading detail...
        </div>
      )}

      {query.error && (
        <div className="flex items-start gap-3 px-4 py-3 border border-neon-red/40 bg-neon-red/5 rounded-sm">
          <AlertTriangle size={16} className="text-neon-red mt-0.5" />
          <p className="text-xs font-mono text-neon-red">
            Failed to load: {(query.error as Error).message}
          </p>
        </div>
      )}

      {data && kpis && (
        <div className="space-y-5">
          {/* ── KPI strip — 7 cells in one row on lg+, wraps on mobile ──── */}
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
            <KpiCell
              label="Clicks"
              value={formatNumber(kpis.total)}
              accent="cyan"
            />
            <KpiCell
              label="Quality"
              value={formatNumber(kpis.quality)}
              accent="green"
            />
            <KpiCell
              label="Bots"
              value={formatNumber(kpis.bots)}
              accent={kpis.botRate > 10 ? 'red' : 'tertiary'}
              hint={kpis.total > 0 ? `${kpis.botRate.toFixed(1)}%` : undefined}
            />
            <KpiCell
              label="Convs"
              value={formatNumber(kpis.convs)}
              accent="magenta"
            />
            <KpiCell
              label="CR"
              value={formatPercent(kpis.cr, 2)}
              accent="green"
            />
            <KpiCell
              label="Revenue"
              value={formatCurrency(kpis.revenue)}
              accent="yellow"
            />
            <KpiCell
              label="EPC"
              value={formatCurrency(kpis.epc)}
              accent="yellow"
            />
          </div>

          {/* ── Bot rate alert — surfaces fraud risk above the fold ────── */}
          {kpis.botRate > 10 && (
            <div className="flex items-center gap-2 px-3 py-2 border border-neon-red/40 bg-neon-red/5 rounded text-[11px] font-mono text-neon-red">
              <Bot size={14} />
              <span>
                Bot rate <strong>{kpis.botRate.toFixed(1)}%</strong> — exceeds
                10% threshold. Investigate traffic source quality.
              </span>
            </div>
          )}

          {/* ── Time series chart — clicks + conversions per day ───────── */}
          <Section
            icon={<TrendingUp size={12} />}
            title={`Time Series — last ${data.days} ${data.days === 1 ? 'day' : 'days'}`}
          >
            {data.timeseries.length === 0 ? (
              <EmptyHint>no activity in this time window</EmptyHint>
            ) : (
              <TimeSeriesBars rows={data.timeseries} />
            )}
          </Section>

          {/* ── Compact dimension tables: Offer + Device + Source side-by-side.
              These typically have few rows (offer count is low, device max
              ~3, source max ~7) so 3-col grid keeps them aligned and dense. */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Section icon={<Tag size={12} />} title="By Offer">
              {data.byOffer.length === 0 ? (
                <EmptyHint>no offer data yet</EmptyHint>
              ) : (
                <DimTable
                  rows={data.byOffer.slice(0, 10).map((r) => ({
                    label:
                      r.offerId == null ? (
                        <span className="text-text-tertiary italic">
                          unlinked
                        </span>
                      ) : (
                        <span className="text-text-secondary">
                          {offerLookup.getName(r.offerId)}
                        </span>
                      ),
                    clicks: r.clicksTotal,
                    quality: r.clicksQuality,
                    convs: r.conversions ?? 0,
                    revenue: r.revenue ?? 0,
                  }))}
                />
              )}
            </Section>

            <Section icon={<Smartphone size={12} />} title="By Device">
              {data.byDevice.length === 0 ? (
                <EmptyHint>no device data</EmptyHint>
              ) : (
                <DimTable
                  rows={data.byDevice.slice(0, 10).map((r) => ({
                    label: r.device || (
                      <span className="text-text-tertiary italic">unknown</span>
                    ),
                    clicks: r.clicksTotal,
                    quality: r.clicksQuality,
                  }))}
                  showConversions={false}
                />
              )}
            </Section>

            <Section icon={<Wifi size={12} />} title="By Source">
              {data.bySource.length === 0 ? (
                <EmptyHint>
                  no source data — most clicks came in without referrer
                </EmptyHint>
              ) : (
                <DimTable
                  rows={data.bySource.slice(0, 10).map((r) => ({
                    label: r.source || (
                      <span className="text-text-tertiary italic">direct</span>
                    ),
                    clicks: r.clicksTotal,
                    quality: r.clicksQuality,
                  }))}
                  showConversions={false}
                />
              )}
            </Section>
          </div>

          {/* ── By Country — full width on its own row. Country list gets
              long (32+ rows for high-traffic publisher) so we give it
              breathing room with a 2-column inner layout on lg+ to fit
              more data without forcing the modal to scroll. */}
          {/* Title clarifies "top N of M" so user doesn't expect to see all
              (50) countries while the table shows only 30. */}
          <Section
            icon={<Globe size={12} />}
            title={
              data.byCountry.length > 30
                ? `By Country (top 30 of ${data.byCountry.length})`
                : `By Country (${data.byCountry.length})`
            }
          >
            {data.byCountry.length === 0 ? (
              <EmptyHint>no traffic data yet</EmptyHint>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-6 gap-y-0">
                {/* Split country list into two halves so wide screens render
                    side-by-side. Top half on the left, bottom half on the right. */}
                {(() => {
                  const rows = data.byCountry.slice(0, 30).map((r) => ({
                    label: r.country ? (
                      <CountryFlag iso2={r.country} showCode size={16} />
                    ) : (
                      <span className="text-text-tertiary italic">unknown</span>
                    ),
                    clicks: r.clicksTotal,
                    quality: r.clicksQuality,
                    convs: r.conversions ?? 0,
                    revenue: r.revenue ?? 0,
                  }));
                  const mid = Math.ceil(rows.length / 2);
                  return (
                    <>
                      <DimTable rows={rows.slice(0, mid)} />
                      {rows.length > mid && <DimTable rows={rows.slice(mid)} />}
                    </>
                  );
                })()}
              </div>
            )}
            {data.byCountry.length > 30 && (
              <p className="text-[10px] font-mono text-text-tertiary mt-2 px-1">
                Showing top 30 of {data.byCountry.length} countries (sorted by
                revenue, then clicks).
              </p>
            )}
          </Section>

          {/* ── Daily breakdown table ──────────────────────────────────── */}
          <Section icon={<Activity size={12} />} title="Daily Breakdown">
            {data.timeseries.length === 0 ? (
              <EmptyHint>no activity in this time window</EmptyHint>
            ) : (
              <table className="w-full text-xs font-mono">
                <thead>
                  <tr className="text-text-tertiary text-[10px] uppercase border-b border-border-subtle">
                    <th className="text-left py-1.5">Date</th>
                    <th className="text-right">Clicks</th>
                    <th className="text-right">Quality</th>
                    <th className="text-right">Convs</th>
                    <th className="text-right">CR</th>
                    <th className="text-right">Revenue</th>
                  </tr>
                </thead>
                <tbody>
                  {data.timeseries
                    .slice()
                    .reverse()
                    .map((row, i) => {
                      const cr =
                        row.clicksQuality > 0
                          ? (row.conversions / row.clicksQuality) * 100
                          : 0;
                      return (
                        <tr
                          key={i}
                          className="border-b border-border-subtle/40"
                        >
                          <td className="py-1.5 text-text-secondary">
                            {row.date}
                          </td>
                          <td className="text-right text-text-primary numeric">
                            {formatNumber(row.clicksTotal)}
                          </td>
                          <td className="text-right text-neon-cyan numeric">
                            {formatNumber(row.clicksQuality)}
                          </td>
                          <td className="text-right text-neon-magenta numeric">
                            {formatNumber(row.conversions)}
                          </td>
                          <td className="text-right text-neon-green numeric">
                            {row.clicksQuality > 0 ? formatPercent(cr, 2) : '—'}
                          </td>
                          <td className="text-right text-neon-yellow numeric">
                            {formatCurrency(row.revenue)}
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            )}
          </Section>
        </div>
      )}
    </Modal>
  );
}

// ─── Subcomponents ─────────────────────────────────────────────────────────

function KpiCell({
  label,
  value,
  accent,
  hint,
}: {
  label: string;
  value: string;
  accent: 'magenta' | 'cyan' | 'yellow' | 'red' | 'green' | 'tertiary';
  hint?: string;
}) {
  const colorMap = {
    magenta: 'text-neon-magenta',
    cyan: 'text-neon-cyan',
    yellow: 'text-neon-yellow',
    red: 'text-neon-red',
    green: 'text-neon-green',
    tertiary: 'text-text-tertiary',
  };
  return (
    <div className="px-2 py-1.5 bg-bg-tertiary border border-border-subtle rounded">
      <div className="text-[9px] font-display uppercase tracking-wider text-text-secondary">
        {label}
      </div>
      <div
        className={`text-base font-display font-bold numeric ${colorMap[accent]}`}
      >
        {value}
      </div>
      {hint && (
        <div className="text-[9px] text-text-tertiary font-mono">{hint}</div>
      )}
    </div>
  );
}

function Section({
  icon,
  title,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <h4 className="text-[10px] font-display uppercase tracking-wider text-neon-magenta flex items-center gap-1 mb-2">
        {icon}
        {title}
      </h4>
      <div className="px-3 py-2 bg-bg-tertiary/50 border border-border-subtle rounded">
        {children}
      </div>
    </div>
  );
}

function EmptyHint({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-[11px] font-mono text-text-tertiary italic py-1">
      {children}
    </p>
  );
}

/**
 * Compact dimension table — same layout reused for country/offer/device/source.
 * Columns: label · clicks · quality (bots derivable) · convs (optional) · CR · revenue.
 * `showConversions` controls whether the convs/CR/revenue columns render —
 * device/source breakdowns don't have conversion data attributed to them
 * (D1 conversions don't track device/source), so we suppress those columns.
 */
function DimTable({
  rows,
  showConversions = true,
}: {
  rows: Array<{
    label: React.ReactNode;
    clicks: number;
    quality: number;
    convs?: number;
    revenue?: number;
  }>;
  showConversions?: boolean;
}) {
  return (
    <table className="w-full text-xs font-mono">
      <thead>
        <tr className="text-text-tertiary text-[10px] uppercase">
          <th className="text-left py-1">Label</th>
          <th className="text-right">Clicks</th>
          {/* Unified column label across modal — was "Conv" here vs "Convs"
              in Daily Breakdown, looked like a typo to user. */}
          {showConversions && <th className="text-right">Convs</th>}
          {showConversions && <th className="text-right">CR</th>}
          {showConversions && <th className="text-right">Rev</th>}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => {
          const cr =
            row.quality > 0 && (row.convs ?? 0) > 0
              ? ((row.convs ?? 0) / row.quality) * 100
              : 0;
          return (
            <tr key={i} className="border-b border-border-subtle/40">
              <td className="py-1.5">{row.label}</td>
              <td className="text-right text-neon-cyan numeric">
                {formatNumber(row.clicks)}
              </td>
              {showConversions && (
                <td className="text-right text-neon-magenta numeric">
                  {formatNumber(row.convs ?? 0)}
                </td>
              )}
              {showConversions && (
                <td className="text-right text-neon-green numeric">
                  {row.quality > 0 && (row.convs ?? 0) > 0
                    ? formatPercent(cr, 1)
                    : '—'}
                </td>
              )}
              {showConversions && (
                <td className="text-right text-neon-yellow numeric">
                  {formatCurrency(row.revenue ?? 0)}
                </td>
              )}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

/**
 * Pure-CSS bar chart for the time series. Two stacked bars per day:
 *   - Clicks (cyan, taller, in background)
 *   - Conversions (magenta, smaller, in front)
 * Heights normalized against the max of each metric independently so a low
 * conversion count is still visible alongside a tall click bar.
 *
 * Avoids pulling in recharts (~50KB gzipped) for a chart this simple.
 */
function TimeSeriesBars({
  rows,
}: {
  rows: Array<{ date: string; clicksTotal: number; conversions: number }>;
}) {
  const maxClicks = Math.max(1, ...rows.map((r) => r.clicksTotal));
  const maxConvs = Math.max(1, ...rows.map((r) => r.conversions));

  return (
    <div>
      {/* Legend */}
      <div className="flex items-center gap-3 text-[10px] font-mono text-text-tertiary mb-2">
        <span className="flex items-center gap-1">
          <span className="inline-block w-2 h-2 bg-neon-cyan/60" /> Clicks
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block w-2 h-2 bg-neon-magenta/80" />{' '}
          Conversions
        </span>
        <span className="ml-auto text-text-tertiary">
          max C: {maxClicks} · max V: {maxConvs}
        </span>
      </div>

      {/* Bars — BUGFIX: previously `flex-1 flex items-end` with `w-full` inner
          made each bar take only ~1px when only 2 rows existed (flex math
          collapsed the inner relative div to content height). Switched to
          fixed h-32 container + per-bar absolute positioning from bottom, so
          heights compute against the actual container height. */}
      <div className="flex items-end gap-1 h-32 px-1 border-b border-border-subtle/40 pb-px">
        {rows.map((r, i) => {
          const clicksH = (r.clicksTotal / maxClicks) * 100;
          const convsH = (r.conversions / maxConvs) * 100;
          return (
            <div
              key={i}
              className="flex-1 h-full relative group"
              title={`${r.date}: ${r.clicksTotal} clicks · ${r.conversions} conv`}
            >
              {/* Tooltip on hover — pure CSS, no JS state */}
              <div className="absolute bottom-full mb-1 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity bg-bg-secondary border border-neon-cyan/40 rounded px-2 py-1 text-[9px] font-mono whitespace-nowrap z-10 pointer-events-none">
                <div className="text-text-primary">{r.date}</div>
                <div className="text-neon-cyan">{r.clicksTotal} clicks</div>
                <div className="text-neon-magenta">{r.conversions} conv</div>
              </div>
              {/* Clicks bar — full width, anchored to bottom of bar container */}
              <div
                className="absolute bottom-0 left-0 right-0 bg-neon-cyan/40 rounded-t-sm transition-all group-hover:bg-neon-cyan/60"
                style={{
                  height: `${clicksH}%`,
                  minHeight: r.clicksTotal > 0 ? '2px' : '0',
                }}
              />
              {/* Conversions bar — center half-width, on top of clicks */}
              <div
                className="absolute bottom-0 left-1/4 right-1/4 bg-neon-magenta/80 rounded-t-sm"
                style={{
                  height: `${convsH}%`,
                  minHeight: r.conversions > 0 ? '2px' : '0',
                }}
              />
            </div>
          );
        })}
      </div>

      {/* X-axis labels — show only first/middle/last to avoid crowding */}
      <div className="flex justify-between text-[9px] font-mono text-text-tertiary mt-1 px-1">
        <span>{rows[0]?.date ?? ''}</span>
        {rows.length > 2 && (
          <span>{rows[Math.floor(rows.length / 2)]?.date ?? ''}</span>
        )}
        <span>{rows[rows.length - 1]?.date ?? ''}</span>
      </div>
    </div>
  );
}
